import {
  gradeBenchPanelState,
  defaultCQAProjectConfiguration
} from '../../state';
import {
  createCQATransaction,
  fetchQaRule,
  fetchRiskCategories,
  resequenceCode,
  loadCQAItemFromURL,
  refreshSpanCompletion,
  resolveCQAWorkitem,
  ReduxActions,
  fetchCodeDescriptions,
  deleteCQATransaction,
  deleteCQACode,
  retrieveAssociatedWorkitems,
  fetchWorkerInfoForUser,
  getGraderWorkItemActivity,
  addEmptySpan,
  attachCodeToSpan,
  detachCodeFromSpan,
  deleteSpan,
  clearRubricVersion,
  fetchRubricVersion,
  updateAttribute,
  fetchAttributes,
  deleteAttribute,
  fetchAllAttributes,
  clearDownstreamAttributes,
  setValidationResult,
  setCaseLevelComplete,
  setPendingSpanNav,
  setCodeCompletion,
  setManualActiveCode,
  bulkResequenceCodes
} from '../../../../actions/grade-bench-panel-actions';
import { resetCommonState } from '../../../../actions/ui.actions';
import { createReducer, AnyAction } from '@reduxjs/toolkit';
import { codePredicates } from '../../transaction-updates.dto-factory';
import { ChargeSessionCodeType } from '../../../../enums';
import { AssesmentSelect, RoleSelect } from '../../fixture';
import { processRiskCategories } from './risk-categories';
import { findSpanById, findCodesBySpanId } from '../../utils/annotation/span-utils';
import * as cmxDateTime from '../../../../core/date-time/cmx-date-time';

const { isSelectionCPT, isSelectionICD, isSelectionModifier } = codePredicates;

const updateAllCodes = (
  invoices: Accelerate.CQAInvoice[],
  modifyCode: (codes: any[], entityType: string) => any[]
) => {
  return invoices.map(invoice => {
    // Map through transactions to update the specific transaction
    const updatedTransactions = invoice.transactions.map(
      (transaction: Accelerate.CQATransaction) => {
        // Use modifyCode to transform the codes array. entityType lets callers
        // disambiguate code_procedure/code_diagnosis/code_modifier (CDMX-17055).
        const procedures = modifyCode(transaction.procedures, 'code_procedure');
        const diagnoses = modifyCode(transaction.diagnoses, 'code_diagnosis');
        const modifiers = modifyCode(transaction.modifiers, 'code_modifier');

        // Return the updated transaction with the modified codes
        return { ...transaction, procedures, diagnoses, modifiers };
      }
    );

    // Return the updated invoice with the new transactions
    return { ...invoice, transactions: updatedTransactions };
  });
};

/**
 * Updates attributes on a specific code across all invoices/transactions.
 * Wraps updateAllCodes with ID matching and null-safe attribute handling.
 */
const updateCodeAttributes = (
  invoices: Accelerate.CQAInvoice[],
  entityId: number,
  entityType: string,
  updateFn: (attributes: CQA.Attribute[]) => CQA.Attribute[]
) => {
  // [CDMX-17055] only touch codes of the matching entityType — code ids are not unique across
  // code_procedure/code_diagnosis/code_modifier, so id alone cross-contaminates same-id codes.
  return updateAllCodes(invoices, (codes, codeEntityType) =>
    codeEntityType === entityType
      ? codes.map(code =>
          code.id === entityId
            ? { ...code, attributes: updateFn(code.attributes || []) }
            : code
        )
      : codes
  );
};

/**
 * Updates rubricAttributes on spans within code arrays (procedures, diagnoses, modifiers).
 */
const updateSpansInCodes = (
  codes: any[],
  spanId: number,
  updateFn: (attributes: CQA.Attribute[]) => CQA.Attribute[]
) => {
  return codes.map(code => ({
    ...code,
    spans: code.spans?.map(span =>
      span.id === spanId
        ? { ...span, rubricAttributes: updateFn(span.rubricAttributes || []) }
        : span
    ) || []
  }));
};

/**
 * Updates rubricAttributes on a specific span across all locations (looseSpans and code spans in invoices).
 * Spans can exist in looseSpans (unattached) or nested inside code.spans (attached to codes).
 */
const updateSpanRubricAttributes = (
  invoices: Accelerate.CQAInvoice[],
  looseSpans: Accelerate.SpanAnnotation[],
  spanId: number,
  updateFn: (attributes: CQA.Attribute[]) => CQA.Attribute[]
): { invoices: Accelerate.CQAInvoice[]; looseSpans: Accelerate.SpanAnnotation[] } => {
  const updatedLooseSpans = looseSpans.map(span =>
    span.id === spanId
      ? { ...span, rubricAttributes: updateFn(span.rubricAttributes || []) }
      : span
  );

  const updatedInvoices = invoices.map(invoice => ({
    ...invoice,
    transactions: invoice.transactions.map(transaction => ({
      ...transaction,
      procedures: updateSpansInCodes(transaction.procedures, spanId, updateFn),
      diagnoses: updateSpansInCodes(transaction.diagnoses, spanId, updateFn),
      modifiers: updateSpansInCodes(transaction.modifiers, spanId, updateFn),
    }))
  }));

  return { invoices: updatedInvoices, looseSpans: updatedLooseSpans };
};

// Merge only the per-span workflowComplete flag (by id) into existing spans, leaving
// rubricAttributes and all other span/code/invoice state untouched.
const applySpanCompletion = (
  span: Accelerate.SpanAnnotation,
  completionById: Map<number, boolean | undefined>
): Accelerate.SpanAnnotation =>
  completionById.has(span.id)
    ? { ...span, workflowComplete: completionById.get(span.id) }
    : span;

const mergeSpanCompletionInCodes = (
  codes: any[],
  completionById: Map<number, boolean | undefined>
) =>
  (codes || []).map(code => ({
    ...code,
    spans:
      code.spans?.map((s: Accelerate.SpanAnnotation) => applySpanCompletion(s, completionById)) || []
  }));

const mergeSpanWorkflowCompletion = (
  invoices: Accelerate.CQAInvoice[],
  looseSpans: Accelerate.SpanAnnotation[],
  completionById: Map<number, boolean | undefined>
): { invoices: Accelerate.CQAInvoice[]; looseSpans: Accelerate.SpanAnnotation[] } => ({
  looseSpans: (looseSpans || []).map(s => applySpanCompletion(s, completionById)),
  invoices: (invoices || []).map(invoice => ({
    ...invoice,
    transactions: invoice.transactions.map(transaction => ({
      ...transaction,
      procedures: mergeSpanCompletionInCodes(transaction.procedures, completionById),
      diagnoses: mergeSpanCompletionInCodes(transaction.diagnoses, completionById),
      modifiers: mergeSpanCompletionInCodes(transaction.modifiers, completionById)
    }))
  }))
});

const collectSpanCompletion = (
  graderWorkItem: Accelerate.CQAGraderWorkItem
): Map<number, boolean | undefined> => {
  const completionById = new Map<number, boolean | undefined>();
  const collect = (spans?: Accelerate.SpanAnnotation[]) =>
    (spans || []).forEach(s => completionById.set(s.id, s.workflowComplete));
  collect(graderWorkItem.looseSpans);
  (graderWorkItem.invoices || []).forEach(invoice =>
    (invoice.transactions || []).forEach(transaction =>
      [transaction.procedures, transaction.diagnoses, transaction.modifiers].forEach(codes =>
        (codes || []).forEach((code: any) => collect(code.spans))
      )
    )
  );
  return completionById;
};

const getSpanScopeAttributeId = (attribute: CQA.Attribute): number | null | undefined =>
  attribute.spanScopeAttributeId ?? attribute.parentQuestionId;

const matchesAttributeSpanScope = (
  attribute: CQA.Attribute,
  spanScopeAttributeId?: number | null
): boolean => spanScopeAttributeId != null
  ? getSpanScopeAttributeId(attribute) === spanScopeAttributeId
  : getSpanScopeAttributeId(attribute) == null;

const mergeFetchedAttributes = (
  existingAttributes: CQA.Attribute[],
  fetchedAttributes: CQA.Attribute[]
): CQA.Attribute[] => {
  const preservedExisting = existingAttributes.filter(existing =>
    !fetchedAttributes.some(fetched =>
      fetched.questionId === existing.questionId &&
      matchesAttributeSpanScope(existing, getSpanScopeAttributeId(fetched))
    )
  );

  return [...preservedExisting, ...fetchedAttributes];
};

const removeSpanIdFromAttributes = (
  attributes: CQA.Attribute[] | undefined,
  spanId: number
): CQA.Attribute[] => {
  return (attributes || []).flatMap(attribute => {
    if (!attribute.spanAnnotationIds?.includes(spanId)) {
      return [attribute];
    }

    const spanAnnotationIds = attribute.spanAnnotationIds.filter(id => id !== spanId);
    if (spanAnnotationIds.length === 0) {
      return [];
    }

    return [{ ...attribute, spanAnnotationIds }];
  });
};

const removeDeletedSpanFromCode = <T extends Accelerate.CQACodeCommon>(
  code: T,
  spanId: number
): T => ({
  ...code,
  spans: code.spans?.filter(span => span.id !== spanId) || [],
  attributes: removeSpanIdFromAttributes(code.attributes, spanId)
});

const addCodeToInvoice = (
  //Removing updateCodes as we pull in api
  invoices: Accelerate.CQAInvoice[],
  codeInfo: Accelerate.CodeAddActionPayload,
  modifyCodes: (code: Accelerate.CQACode[]) => any[]
) => {
  return invoices.map(invoice => {
    // Check if this is the invoice to update
    if (invoice.id !== codeInfo.context.invoice.id) {
      return invoice; // Return the invoice unchanged if it's not the target
    }

    // Map through transactions to update the specific transaction
    const updatedTransactions = invoice.transactions.map(
      (transaction: Accelerate.CQATransaction) => {
        // Check if this is the transaction to update
        if (transaction.id !== codeInfo.context.transaction.id) {
          return transaction; // Return the transaction unchanged if it's not the target
        }

        // Use modifyCode to transform the codes array
        const updatedTransaction = { ...transaction };
        if (isSelectionCPT(codeInfo.codeInfo)) {
          updatedTransaction.procedures = modifyCodes(transaction.procedures);
        }

        if (isSelectionICD(codeInfo.codeInfo)) {
          updatedTransaction.diagnoses = modifyCodes(transaction.diagnoses);
        }

        if (isSelectionModifier(codeInfo.codeInfo)) {
          updatedTransaction.modifiers = modifyCodes(transaction.modifiers);
        }

        // Return the updated transaction with the modified codes
        return updatedTransaction;
      }
    );

    // Return the updated invoice with the new transactions
    return { ...invoice, transactions: updatedTransactions };
  });
};

const updateCode = (
  //Removing updateCodes as we pull in api
  invoices: Accelerate.CQAInvoice[],
  changeEvent: CMxCommonApp.GradeBenchSelectEvent,
  modifyCodes: (code: Accelerate.CQACode[]) => any[]
) => {
  return invoices.map(invoice => {
    // Check if this is the invoice to update
    if (invoice.id !== changeEvent.context.invoice.id) {
      return invoice; // Return the invoice unchanged if it's not the target
    }

    // Map through transactions to update the specific transaction
    const updatedTransactions = invoice.transactions.map(
      (transaction: Accelerate.CQATransaction) => {
        // Check if this is the transaction to update
        if (transaction.id !== changeEvent.context.transaction.id) {
          return transaction; // Return the transaction unchanged if it's not the target
        }

        // Use modifyCode to transform the codes array
        const updatedTransaction = { ...transaction };
        if (changeEvent.context.value.codeType == 'procedure') {
          updatedTransaction.procedures = modifyCodes(transaction.procedures);
        }

        if (changeEvent.context.value.codeType == 'diagnosis') {
          updatedTransaction.diagnoses = modifyCodes(transaction.diagnoses);
        }

        if (changeEvent.context.value.codeType == 'modifier') {
          updatedTransaction.modifiers = modifyCodes(transaction.modifiers);
        }

        // Return the updated transaction with the modified codes
        return updatedTransaction;
      }
    );

    // Return the updated invoice with the new transactions
    return { ...invoice, transactions: updatedTransactions };
  });
};

const filterCQACodeAndOrder = <T extends Accelerate.CQACodeCommon>(
  cqaCode: Accelerate.CQACode,
  codes: T[]
) => {
  if (codes.length === 0) {
    return [];
  }

  if (!cqaCode) {
    return codes;
  }

  const deletedSequenceNumber = cqaCode.sequenceNumber;

  return codes
    .filter(code => code.id !== cqaCode.id) // Filter out the deleted code
    .map(code => {
      // Decrement sequence_number for codes with higher sequence numbers
      if (code.sequenceNumber > deletedSequenceNumber) {
        return { ...code, sequenceNumber: code.sequenceNumber - 1 };
      }
      return code;
    });
};

//Different state and reducer but still share when you can!
const gradeBenchPanel = createReducer(gradeBenchPanelState, builder => {
  builder.addCase(resetCommonState.type, () => ({ ...gradeBenchPanelState }));

  builder.addCase(ReduxActions.updateOrAddAssessment.fulfilled, (state, action) => {
    const result = action.payload;
    const params = action.meta.arg;
    const originalState = { ...state };
    const { value } = params.context;

    // Modify the code in the state — prefer stable id when the selection
    // includes it (duplicate code strings on one invoice); else match code.
    const modifyExistingCode = (codes: Accelerate.CQACode[]) => {
      return codes.map(code => {
        const matches =
          value.id == null ? code.code === value.code : code.id === value.id;
        return matches ? { ...code, codeAssessment: result } : code;
      });
    };

    const updatedInvoices = updateCode(
      originalState.invoices,
      params,
      modifyExistingCode
    );

    return {
      ...state,
      invoices: updatedInvoices
    };
  });

  const addCodeHandler = (state, action) => {
    const result = action.payload;
    const params = action.meta.arg;
    const originalState = { ...state };

    const addNewCode = (codes: Accelerate.CQACode[]) => {
      return [
        ...codes,
        { ...result, description: params.codeInfo.description }
      ];
    };

    const updatedInvoices = addCodeToInvoice(
      originalState.invoices,
      params,
      addNewCode
    );

    const codeDescriptionsWithNewCode = {
      ...state.codeDescriptions,
      [params.codeInfo.code]: params.codeInfo
    };

    return {
      ...state,
      invoices: updatedInvoices,
      codeDescriptionsWithNewCode
    };
  };
  builder.addCase(ReduxActions.addDiagnosis.fulfilled, addCodeHandler);
  builder.addCase(ReduxActions.addProcedure.fulfilled, addCodeHandler);
  builder.addCase(ReduxActions.addModifier.fulfilled, addCodeHandler);

  builder.addCase(deleteCQACode.fulfilled, (state, action) => {
    const deleted = action.payload;
    const inputParams = action.meta.arg;
    if (!deleted) {
      return {
        ...state
      };
    }

    // Extract spans from the code being deleted before filtering it out
    // We need to find the code in the current state to get its spans
    let deletedCodeSpans: Accelerate.SpanAnnotation[] = [];
    const codeToDelete = inputParams.code;

    // Find the code in the state to extract its spans
    state.invoices.forEach(invoice => {
      if (invoice.id === inputParams.context.invoice.id) {
        invoice.transactions.forEach(transaction => {
          if (transaction.id === inputParams.context.transaction.id) {
            let codeArray: Accelerate.CQACode[] = [];
            if (inputParams.codeType === 'procedure') {
              codeArray = transaction.procedures || [];
            } else if (inputParams.codeType === 'diagnosis') {
              codeArray = transaction.diagnoses || [];
            } else if (inputParams.codeType === 'modifier') {
              codeArray = transaction.modifiers || [];
            }

            const foundCode = codeArray.find(c => c.id === codeToDelete.id);
            if (foundCode && foundCode.spans) {
              deletedCodeSpans = [...foundCode.spans];
            }
          }
        });
      }
    });

    // Process spans: check if they're shared with other codes
    const orphanedSpans: Accelerate.SpanAnnotation[] = [];

    deletedCodeSpans.forEach(span => {
      // Find all codes that have this span attached
      const codesWithSpan = findCodesBySpanId(state.invoices, span.id);

      // Filter out the code being deleted
      const otherCodesWithSpan = codesWithSpan.filter(
        codeInfo => !(
          codeInfo.code.id === codeToDelete.id &&
          codeInfo.invoice.id === inputParams.context.invoice.id &&
          codeInfo.transaction.id === inputParams.context.transaction.id
        )
      );

      // If no other codes have this span, it's orphaned and should go to looseSpans
      if (otherCodesWithSpan.length === 0) {
        orphanedSpans.push(span);
      }
    });

    // Update invoices by filtering out the deleted code
    const updatedInvoices = state.invoices.map(invoice => {
      if (invoice.id !== inputParams.context.invoice.id) {
        return invoice;
      }

      const updatedTransactions = invoice.transactions.map(transaction => {
        if (transaction.id !== inputParams.context.transaction.id) {
          return transaction;
        }

        if (inputParams.codeType === 'procedure') {
          return {
            ...transaction,
            procedures: filterCQACodeAndOrder(
              inputParams.code,
              transaction.procedures
            )
          };
        }

        if (inputParams.codeType === 'diagnosis') {
          return {
            ...transaction,
            diagnoses: filterCQACodeAndOrder(
              inputParams.code,
              transaction.diagnoses
            )
          };
        }

        if (inputParams.codeType === 'modifier') {
          return {
            ...transaction,
            modifiers: filterCQACodeAndOrder(
              inputParams.code,
              transaction.modifiers
            )
          };
        }

        return {
          ...transaction
        };
      });

      return {
        ...invoice,
        transactions: updatedTransactions
      };
    });

    // Add orphaned spans to looseSpans
    const updatedLooseSpans = [...state.looseSpans, ...orphanedSpans];

    return {
      ...state,
      invoices: updatedInvoices,
      looseSpans: updatedLooseSpans
    };
  });

  builder.addCase(resequenceCode.fulfilled, (state, action) => {
    const result = action.payload;
    if (result == null) {
      return { ...state };
    }
    const params = action.meta.arg;
    const originalState = { ...state };
    const { context, code } = params;
    const updatedInvoices = originalState.invoices.map(invoice => {
      // Check if this is the invoice to update
      if (invoice.id !== context.invoice.id) {
        return invoice; // Return the invoice unchanged if it's not the target
      }

      // Map through transactions to update the specific transaction
      const updatedTransactions = invoice.transactions.map(
        (transaction: Accelerate.CQATransaction) => {
          // Check if this is the transaction to update
          if (transaction.id !== context.transaction.id) {
            return transaction; // Return the transaction unchanged if it's not the target
          }

          const map = {
            procedure: 'procedures',
            diagnosis: 'diagnoses',
            modifier: 'modifiers'
          };

          const currentCodes = transaction[map[code.codeType]] as Accelerate.CQACode[];

          // The cqa-service does not return descriptions, spans, attributes, or codeAssessment
          // for the reordered codes. Preserve them from current state (find by id, else by code) so that
          // reordering does not reset the workflow form.
          const resultCodes = result as unknown as Array<Accelerate.CQACode>;
          const resultsWithPreservedState = resultCodes.map(reorderedCode => {
            const current =
              reorderedCode.id !== undefined
                ? currentCodes.find((c: Accelerate.CQACode) => c.id === reorderedCode.id)
                : currentCodes.find((c: Accelerate.CQACode) => c.code === reorderedCode.code);
            return {
              ...reorderedCode,
              description: current?.description,
              spans: current?.spans ?? [],
              attributes: current?.attributes ?? [],
              codeAssessment: current?.codeAssessment ?? reorderedCode.codeAssessment
            };
          });

          const updatedTransaction = { ...transaction };
          updatedTransaction[map[code.codeType]] = resultsWithPreservedState;

          // Return the updated transaction with the modified codes
          return updatedTransaction;
        }
      );

      // Return the updated invoice with the new transactions
      return { ...invoice, transactions: updatedTransactions };
    });

    return {
      ...originalState,
      invoices: updatedInvoices
    };
  });

  builder.addCase(bulkResequenceCodes.fulfilled, (state, action) => {
    const result = action.payload;
    if (result == null) {
      return { ...state };
    }
    const { context } = action.meta.arg;
    const codeMap = { procedure: 'procedures', diagnosis: 'diagnoses', modifier: 'modifiers' } as const;

    const updatedInvoices = state.invoices.map(invoice => {
      if (invoice.id !== context.invoice.id) return invoice;

      const updatedTransactions = invoice.transactions.map(
        (transaction: Accelerate.CQATransaction) => {
          if (transaction.id !== context.transaction.id) return transaction;

          const updatedTransaction = { ...transaction };

          if (result.procedures != null) {
            const currentCodes = transaction[codeMap.procedure] as Accelerate.CQAProcedure[];
            const resultCodes = result.procedures as unknown as Array<Accelerate.CQAProcedure>;
            updatedTransaction[codeMap.procedure] = resultCodes.map(reorderedCode => {
              const current = reorderedCode.id !== undefined
                ? currentCodes.find((c: Accelerate.CQAProcedure) => c.id === reorderedCode.id)
                : currentCodes.find((c: Accelerate.CQAProcedure) => c.code === reorderedCode.code);
              return {
                ...reorderedCode,
                description: current?.description,
                spans: current?.spans ?? [],
                attributes: current?.attributes ?? [],
                codeAssessment: current?.codeAssessment ?? reorderedCode.codeAssessment,
                units: current?.units ?? reorderedCode.units
              };
            });
          }

          if (result.diagnoses != null) {
            const currentCodes = transaction[codeMap.diagnosis] as Accelerate.CQACode[];
            const resultCodes = result.diagnoses as unknown as Array<Accelerate.CQACode>;
            updatedTransaction[codeMap.diagnosis] = resultCodes.map(reorderedCode => {
              const current = reorderedCode.id !== undefined
                ? currentCodes.find((c: Accelerate.CQACode) => c.id === reorderedCode.id)
                : currentCodes.find((c: Accelerate.CQACode) => c.code === reorderedCode.code);
              return {
                ...reorderedCode,
                description: current?.description,
                spans: current?.spans ?? [],
                attributes: current?.attributes ?? [],
                codeAssessment: current?.codeAssessment ?? reorderedCode.codeAssessment
              };
            });
          }

          if (result.modifiers != null) {
            const currentCodes = transaction[codeMap.modifier] as Accelerate.CQACode[];
            const resultCodes = result.modifiers as unknown as Array<Accelerate.CQACode>;
            updatedTransaction[codeMap.modifier] = resultCodes.map(reorderedCode => {
              const current = reorderedCode.id !== undefined
                ? currentCodes.find((c: Accelerate.CQACode) => c.id === reorderedCode.id)
                : currentCodes.find((c: Accelerate.CQACode) => c.code === reorderedCode.code);
              return {
                ...reorderedCode,
                description: current?.description,
                spans: current?.spans ?? [],
                attributes: current?.attributes ?? [],
                codeAssessment: current?.codeAssessment ?? reorderedCode.codeAssessment
              };
            });
          }

          return updatedTransaction;
        }
      );

      return { ...invoice, transactions: updatedTransactions };
    });

    return { ...state, invoices: updatedInvoices };
  });

  builder.addCase(refreshSpanCompletion.fulfilled, (state, action) => {
    const completionById = collectSpanCompletion(action.payload);
    const { invoices, looseSpans } = mergeSpanWorkflowCompletion(
      state.invoices,
      state.looseSpans,
      completionById
    );
    return { ...state, invoices, looseSpans };
  });

  // There is a similar reducer for loadCQAItemFromURL action in code-bench.reducer.ts
  builder.addCase(loadCQAItemFromURL.fulfilled, (state, action) => {
    const cqaGraderWorkItem = action.payload;
    const sdCaseId = cqaGraderWorkItem.sdCaseId;
    const workItem = cqaGraderWorkItem.workItem;
    const invoices = cqaGraderWorkItem.invoices;
    const graderUpdatedDate = cqaGraderWorkItem.graderUpdatedDate;
    const assignedToId = cqaGraderWorkItem.assignedToId;

      if (invoices.length) {
        // Copy "invoice metadata" to the first invoice
        // This has to do with how CQA workItems are currently imported as only a single invoice
        invoices[0] = {
          ...invoices[0],
          ...workItem,
          // workitem.id was overriding invoice.id which causes a bug.
          id: invoices[0].id,
          status: cqaGraderWorkItem.status,
          workitemId: workItem.id,
          sdCaseId: sdCaseId,
          // Store loose spans on the first invoice
          looseSpans: cqaGraderWorkItem.looseSpans || [],
          assignedToId,
        };
      }
      return {
        ...state,
        invoices: invoices,
        // Store loose spans in state for easy access
        looseSpans: cqaGraderWorkItem.looseSpans || [],
        caseAttributes: [],
        attributesFetched: false,
        attributesFetchError: false,
        // Drop autosave bookkeeping from the prior case — otherwise its
        // in-flight .fulfilled can match the new case's stale-guard by key
        // and inject its answer into caseAttributes.
        pendingAutosaves: {},
        attributeLatestRequestId: {},
        resolverValidationErrors: null,
        resolverIdealCodes: null,
        caseLevelComplete: null,
        graderUpdatedDate
      };
    }
  );

  builder.addCase(resolveCQAWorkitem.pending, (state) => ({
    ...state,
    resolverValidationErrors: null,
    resolverIsRunning: true
  }));

  builder.addCase(resolveCQAWorkitem.fulfilled, (state, action) => ({
    ...state,
    resolverValidationErrors: action.payload.validationErrors?.length ? action.payload.validationErrors : null,
    resolverIdealCodes: action.payload.skipped ? state.resolverIdealCodes : (action.payload.idealCodes ?? null),
    resolverIsRunning: false
  }));

  builder.addCase(fetchQaRule.pending, (state) => ({
    ...state,
    validationResult: null
  }));

  builder.addCase(fetchQaRule.fulfilled, (state, action) => {
    const qaRule = action.payload;
    // We should not expect qaRule to be not found however we cannot make this assertion for all qaRule Queries
    let updatedQaRule;
    if (qaRule) {
      updatedQaRule = qaRule;
    } else {
      updatedQaRule = {
        ...state.qaRule,
        data: {
          ...state.qaRule.data,
          project_configuration: defaultCQAProjectConfiguration
        }
      };
    }

    const project_configuration =
      qaRule?.data?.project_configuration ?? defaultCQAProjectConfiguration;

    //Creating a fresh object to guarantee a rerender, if there are changes
    const updatedFields: Accelerate.GradeBenchPanelFieldDefinitonState = {
      icdFields: [],
      modifierFields: [],
      cptFields: []
    };

    //Hide assesment or role for all code types
    for (const definitionsKey in state.fieldDefinitions) {
      updatedFields[definitionsKey] = state.fieldDefinitions[
        definitionsKey
      ].map(field => {
        if (
          project_configuration.assessment !== undefined &&
          field.key === AssesmentSelect.key
        ) {
          return { ...field, hidden: !project_configuration.assessment };
        }

        if (
          project_configuration.role !== undefined &&
          field.key === RoleSelect.key
        ) {
          return { ...field, hidden: !project_configuration.role };
        }

        return { ...field };
      });
    }

    //For each field, disable it if the configuration for the codeType is false
    if (project_configuration.procedure !== undefined) {
      updatedFields.cptFields = updatedFields.cptFields.map(cptField => {
        cptField.readonly = !project_configuration.procedure;
        if (
          cptField.key == AssesmentSelect.key ||
          cptField.key == RoleSelect.key
        ) {
          // If `cptField.hidden` from the `Hide assesment or role for all code types` step then it should stay disabled
          cptField.hidden = cptField.hidden || !project_configuration.procedure;
        }
        return cptField;
      });
    }

    if (project_configuration.diagnosis !== undefined) {
      updatedFields.icdFields = updatedFields.icdFields.map(icdField => {
        icdField.readonly = !project_configuration.diagnosis;
        if (
          icdField.key == AssesmentSelect.key ||
          icdField.key == RoleSelect.key
        ) {
          icdField.hidden = icdField.hidden || !project_configuration.diagnosis;
        }
        return icdField;
      });
    }

    if (project_configuration.modifier !== undefined) {
      updatedFields.modifierFields = updatedFields.modifierFields.map(
        modifierField => {
          modifierField.readonly = !project_configuration.modifier;
          if (
            modifierField.key == AssesmentSelect.key ||
            modifierField.key == RoleSelect.key
          ) {
            modifierField.hidden =
              modifierField.hidden || !project_configuration.modifier;
          }
          return modifierField;
        }
      );
    }

    return {
      ...state,
      qaRule: updatedQaRule,
      fieldDefinitions: {
        ...updatedFields
      }
    };
  });

  /*
    The fetchRiskCategories reducer takes each risk category dictionary and create a mapping from assessments to roles
    The final object looks like
    {
      procedure : [assessment : string] : dictioanryValue[] // dictioanryValue[] are roles
      diagnosis : [assessment : string] : dictioanryValue[] // dictioanryValue[] are roles
      modifier : [assessment : string] : dictioanryValue[] // dictioanryValue[] are roles
    }
  */
  builder.addCase(fetchRiskCategories.fulfilled, (state, action) => {
    const result = action.payload;
    const { fields, riskCategories } = processRiskCategories(
      state.fieldDefinitions,
      result
    );

    return {
      ...state,
      fieldDefinitions: {
        ...state.fieldDefinitions,
        modifierFields: fields.modifier,
        icdFields: fields.icd,
        cptFields: fields.cpt
      },
      riskCategories: {
        [ChargeSessionCodeType.PROCEDURE]: riskCategories.procedure,
        [ChargeSessionCodeType.DIAGNOSIS]: riskCategories.diagnosis,
        [ChargeSessionCodeType.MODIFIER]: riskCategories.modifier
      },
      cqaCodeAssessmentRiskDictionaries: {
        procedure: {
          columnNames: result.CptRowData.columnNames,
          rows: result.CptRowData.rowValues.map(row => ({
            assessment: row['ASSESSMENT'],
            category: row['CATEGORY'],
            role: row['ROLE']
          }))
        },
        diagnosis: {
          columnNames: result.IcdRowData.columnNames,
          rows: result.IcdRowData.rowValues.map(row => ({
            assessment: row['ASSESSMENT'],
            category: row['CATEGORY'],
            role: row['ROLE']
          }))
        },
        modifier: {
          columnNames: result.ModRowData.columnNames,
          rows: result.ModRowData.rowValues.map(row => ({
            assessment: row['ASSESSMENT'],
            category: row['CATEGORY'],
            role: row['ROLE'],
            description: row['DESCRIPTION'],
            modifier: row['MODIFIER']
          }))
        }
      }
    };
  });

  builder.addCase(fetchCodeDescriptions.fulfilled, (state, action) => {
    const result = action.payload;
    if (Object.keys(result).length == 0) {
      return { ...state };
    }

    const addDescriptionsToCodes = codes => {
      return codes.map((code: Accelerate.CQACode) => {
        return {
          ...code,
          description: result[code.code]
            ? result[code.code].description
            : undefined
        };
      });
    };
    const updatedInvoices = updateAllCodes(
      state.invoices,
      addDescriptionsToCodes
    );

    // Loose spans don't need code description updates since codes are stored in invoice structure
    const updatedLooseSpans = state.looseSpans;

    //We are storing code descriptions both at the code and as a codeDescriptions object.
    //We should move to just codeDescriptions and have code-layer.tsx recieve this object as a prop and access it
    return {
      ...state,
      invoices: updatedInvoices,
      looseSpans: updatedLooseSpans
    };
  });

  builder.addCase(createCQATransaction.fulfilled, (state, action) => {
    const transactionResponse = action.payload;
    const inputParams = action.meta.arg;
    if (!transactionResponse) {
      return {
        ...state,
        gradingErrors: {
          addTransaction: true
        }
      };
    }

    const newTransaction: Accelerate.CQATransaction = {
      id: transactionResponse.id,
      diagnoses: [],
      modifiers: [],
      procedures: [],
      original: transactionResponse.original,
      sequenceNumber: transactionResponse.sequenceNumber
    };

    // update transactions with newly created transaction for the matched invoice
    const invoices = state.invoices.map(invoice => {
      if (invoice.id !== inputParams.invoiceId) {
        return invoice;
      }

      const updatedTransactions = [...(invoice.transactions ?? []), newTransaction];

      return {
        ...invoice,
        transactions: updatedTransactions
      };
    });
    return {
      ...state,
      invoices,
      gradingErrors: {
        addTransaction: false
      }
    };
  });

  builder.addCase(deleteCQATransaction.fulfilled, (state, action) => {
    const deleted = action.payload;
    const inputParams = action.meta.arg;
    if (!deleted) {
      return {
        ...state
      };
    }

    const invoices = state.invoices.map(invoice => {
      if (invoice.id !== inputParams.invoiceId) {
        return invoice;
      }

      // Remove the transaction and order the remaining transactions
      const updatedTransactions = invoice.transactions
        .filter(transaction => transaction.id !== inputParams.transactionId)
        .map((transaction, index) => {
          // Original transaction should not be modified here
          // and will always be sequenceNumber 1
          if (!transaction.original) {
            return { ...transaction, sequenceNumber: index + 1 };
          }
          return transaction;
        });

      return {
        ...invoice,
        transactions: updatedTransactions
      };
    });

    return {
      ...state,
      invoices
    };
  });

  builder.addCase(retrieveAssociatedWorkitems.fulfilled, (state, action) => {
    const result = action.payload;
    return {
      ...state,
      associatedWorkitems: result
    };
  });

  builder.addCase(fetchWorkerInfoForUser.fulfilled, (state, action) => {
    const result = action.payload;
    return {
      ...state,
      workerInfo: result.data.QueryWorker
    };
  });

  builder.addCase(getGraderWorkItemActivity.fulfilled, (state, action) => {
    const result = action.payload;
    const { activity: graderActivity, comments: coderNotes } = result;
console.log("myLog graderActivity", graderActivity);
    // Filter and transform grader activity
    const transformedActivity = graderActivity
      .filter(item => {
        if ('code' in item) {
          if (
            !item.code?.original &&
            ['ADD', 'DEL'].includes(item.revisionType)
          ) {
            return true;
          }

          // We don't want to show imported codes in activity, but do show if imported codes
          // were deleted or modified.
          if(item.code?.original &&
            ['DEL', 'MOD'].includes(item.revisionType)
          ) {
            return true;
          }

          // Show units for procedures, otherwise MOD for modifier and diagnosis is a lot of noise
          if (['MOD'].includes(item.revisionType)
            && item.entityType === 'procedure'
          ) {
            return true;
          }
        }

        if (
          'codeAssessment' in item &&
          ['ADD', 'MOD'].includes(item.revisionType)
        ) {
          return true;
        }

        if(
          'attribute' in item
        ) {
          return true;
        }

        if (item.entityType.toString() === 'span' && 'span' in item) {
          if (!item.span?.original && ['ADD', 'DEL'].includes(item.revisionType)) {
            return true;
          }

          // We don't want to show imported spans in activity, but do show if imported
          // spans were deleted.
          if (item.span?.original && item.revisionType === 'DEL') {
            return true;
          }
        }

        if(item.entityType.toString() === "codeSpan") {
          return true;
        }

        return false;
      })
      .map(item => ({
        ...item,
        activityType: 'CQA' as const,
        timestamp: item.revInfo.datetime
      }));
    const transformedCoderNotes = (coderNotes || []).map(note => ({
      ...note,
      activityType: note.lookup_type,
      timestamp: note.created_date
    }));

    // Merge and sort
    const mergedActivity = [...transformedActivity, ...transformedCoderNotes].sort(
      (a, b) => cmxDateTime.sortByDate(a.timestamp, b.timestamp)
    );

    return {
      ...state,
      activity: mergedActivity
    };
  });

  builder.addCase(addEmptySpan.fulfilled, (state, action) => {
    const result = action.payload;
    const updatedLooseSpans = [...state.looseSpans, ...result.spans];
    return {
      ...state,
      looseSpans: updatedLooseSpans
    };
  });

  builder.addCase(attachCodeToSpan.fulfilled, (state, action) => {
    const result = action.payload;
    const params = action.meta.arg;
    // First check if span is in loose spans
    let spanToAttach = state.looseSpans.find(span => span.id === params.spanId);
    let spanSource = 'looseSpans';

    // If not in loose spans, search in all code spans using utility function
    if (!spanToAttach) {
      spanToAttach = findSpanById(state.invoices, params.spanId);
      spanSource = 'codeSpans';
    }

    if (!spanToAttach) {
      return state; // Span not found anywhere
    }

    // Find and update the code in invoices
    const updatedInvoices = state.invoices.map(invoice => {
      const updatedTransactions = invoice.transactions.map(transaction => {
        const codeTypeMap = {
          'PROCEDURE': 'procedures',
          'DIAGNOSIS': 'diagnoses',
          'MODIFIER': 'modifiers'
        };

        const codeArray = codeTypeMap[params.codeType];
        if (!codeArray) return transaction;

        const updatedCodes = transaction[codeArray].map(code => {
          if (code.id === params.codeId) {
            // Check if span is already attached to this code
            const existingSpanIndex = code.spans?.findIndex(span => span.id === params.spanId) ?? -1;
            if (existingSpanIndex >= 0) {
              // Span already attached, no change needed
              return code;
            } else {
              // Add new span
              return {
                ...code,
                spans: [...(code.spans ?? []), spanToAttach]
              };
            }
          }
          return code;
        });

        return {
          ...transaction,
          [codeArray]: updatedCodes
        };
      });

      return {
        ...invoice,
        transactions: updatedTransactions
      };
    });

    // Remove span from loose spans only if it was originally there
    const updatedLooseSpans = spanSource === 'looseSpans'
      ? state.looseSpans.filter(span => span.id !== params.spanId)
      : state.looseSpans;

    return {
      ...state,
      invoices: updatedInvoices,
      looseSpans: updatedLooseSpans
    };
  });

  builder.addCase(detachCodeFromSpan.fulfilled, (state, action) => {
    const params = action.meta.arg;
    // Find the span in the code's spans array and move it back to loose spans
    // ONLY if it's not attached to any other codes
    let loosenedSpan = null;

    // First, check if span is attached to any other codes besides this one
    const codesWithSpan = findCodesBySpanId(state.invoices, params.spanId);
    const isAttachedToOtherCodes = codesWithSpan.some(
      ({ code, codeType }) =>
        code.id !== params.codeId || codeType.toLowerCase() !== params.codeType.toLowerCase()
    );

    // Search through all codes to find the span
    const updatedInvoices = state.invoices.map(invoice => {
      const updatedTransactions = invoice.transactions.map(transaction => {
        const codeTypeMap = {
          'PROCEDURE': 'procedures',
          'DIAGNOSIS': 'diagnoses',
          'MODIFIER': 'modifiers'
        };

        const codeArray = codeTypeMap[params.codeType];
        if (!codeArray) return transaction;

        const updatedCodes = transaction[codeArray].map(code => {
          if (code.id === params.codeId) {
            const spanIndex = code.spans?.findIndex(span => span.id === params.spanId);
            if (spanIndex !== undefined && spanIndex >= 0) {
              // Found the span, remove it from code
              const remainingSpans = code.spans.filter(span => span.id !== params.spanId);

              // Only store for moving to loose spans if span is not attached to other codes
              if (!isAttachedToOtherCodes) {
                loosenedSpan = code.spans[spanIndex];
              }

              return {
                ...code,
                spans: remainingSpans
              };
            }
          }
          return code;
        });

        return {
          ...transaction,
          [codeArray]: updatedCodes
        };
      });

      return {
        ...invoice,
        transactions: updatedTransactions
      };
    });

    // If span was found and detached, add it back to loose spans
    let updatedLooseSpans = state.looseSpans;
    if (loosenedSpan) {
      updatedLooseSpans = [...state.looseSpans, loosenedSpan];
    }

    return {
      ...state,
      invoices: updatedInvoices,
      looseSpans: updatedLooseSpans
    };
  });

  builder.addCase(deleteSpan.fulfilled, (state, action) => {
    const params = action.meta.arg;
    // Remove span from both loose spans and code spans
    const updatedLooseSpans = state.looseSpans.filter(span => span.id !== params.spanId);

    // Also remove from any code spans
    const updatedInvoices = state.invoices.map(invoice => {
      const updatedTransactions = invoice.transactions.map(transaction => {
        const updatedProcedures = transaction.procedures.map(procedure =>
          removeDeletedSpanFromCode(procedure, params.spanId)
        );

        const updatedDiagnoses = transaction.diagnoses.map(diagnosis =>
          removeDeletedSpanFromCode(diagnosis, params.spanId)
        );

        const updatedModifiers = transaction.modifiers.map(modifier =>
          removeDeletedSpanFromCode(modifier, params.spanId)
        );

        return {
          ...transaction,
          procedures: updatedProcedures,
          diagnoses: updatedDiagnoses,
          modifiers: updatedModifiers
        };
      });

      return {
        ...invoice,
        transactions: updatedTransactions
      };
    });

    return {
      ...state,
      invoices: updatedInvoices,
      looseSpans: updatedLooseSpans,
      caseAttributes: state.caseAttributes
        ? removeSpanIdFromAttributes(state.caseAttributes, params.spanId)
        : state.caseAttributes
    };
  });

  builder.addCase(clearRubricVersion.type, (state) => {
    return {
      ...state,
      rubricVersion: null
    };
  });

  builder.addCase(fetchRubricVersion.fulfilled, (state, action) => {
    const result = action.payload;
    const params = action.meta.arg;
    const rubricVersionId = state.qaRule?.data?.project_configuration?.rubricVersionId;
    //Rubric version may be stale by the time fetch rubric is done, https://codametrix.atlassian.net/browse/CDMX-10884
    if (rubricVersionId !== params.versionId) {
      return state;
    }
    return {
      ...state,
      rubricVersion: result
    };
  });

  builder.addCase(updateAttribute.pending, (state, action) => {
    const params = action.meta.arg;
    const key = `${params.entityType}:${params.entityId}:${params.questionId}`;
    return {
      ...state,
      pendingAutosaves: { ...(state.pendingAutosaves ?? {}), [action.meta.requestId]: true as true },
      attributeLatestRequestId: { ...(state.attributeLatestRequestId ?? {}), [key]: action.meta.requestId }
    };
  });

  // Aborted thunks dispatch .rejected (not .fulfilled), so this handles
  // cancellation cleanup for both updateAttribute and deleteAttribute below.
  builder.addCase(updateAttribute.rejected, (state, action) => {
    const pending = { ...(state.pendingAutosaves ?? {}) };
    delete pending[action.meta.requestId];
    return { ...state, pendingAutosaves: pending };
  });

  builder.addCase(updateAttribute.fulfilled, (state, action) => {
    const result = action.payload;
    const params = action.meta.arg;
    const pending = { ...(state.pendingAutosaves ?? {}) };
    delete pending[action.meta.requestId];

    // Stale-guard: discard a fulfilled write that was superseded mid-flight.
    // Opt-in — only fires when the slice tracked the .pending dispatch (the
    // map is empty for callers that synthesize .fulfilled directly).
    const key = `${params.entityType}:${params.entityId}:${params.questionId}`;
    const latestRequestId = (state.attributeLatestRequestId ?? {})[key];
    if (latestRequestId !== undefined && action.meta.requestId !== latestRequestId) {
      return { ...state, pendingAutosaves: pending };
    }

    if (params.entityType === 'case') {
      const existingAttributes = state.caseAttributes || [];
      const attributeIndex = existingAttributes.findIndex(
        a => a.questionId === params.questionId
      );
      const caseAttributes = attributeIndex >= 0
        ? existingAttributes.map((a, idx) => idx === attributeIndex ? result : a)
        : [...existingAttributes, result];
      return { ...state, pendingAutosaves: pending, caseAttributes };
    }

    // Handle span-level attributes
    if (params.entityType === 'span') {
      const { invoices: updatedInvoices, looseSpans: updatedLooseSpans } =
        updateSpanRubricAttributes(state.invoices, state.looseSpans, Number(params.entityId), attrs => {
          const idx = attrs.findIndex(a =>
            a.questionId === params.questionId &&
            matchesAttributeSpanScope(a, params.spanScopeAttributeId ?? params.parentQuestionId)
          );
          return idx >= 0
            ? attrs.map((a, i) => i === idx ? result : a)
            : [...attrs, result];
        });
      return { ...state, pendingAutosaves: pending, invoices: updatedInvoices, looseSpans: updatedLooseSpans };
    }

    // Only process code entity types (code_procedure, code_diagnosis, code_modifier)
    if (!params.entityType.startsWith('code_')) return { ...state, pendingAutosaves: pending };

    const updatedInvoices = updateCodeAttributes(state.invoices, Number(params.entityId), params.entityType, attrs => {
      const idx = attrs.findIndex(a => a.questionId === params.questionId);
      return idx >= 0
        ? attrs.map((a, i) => i === idx ? result : a)
        : [...attrs, result];
    });

    return { ...state, pendingAutosaves: pending, invoices: updatedInvoices };
  });

  builder.addCase(fetchAttributes.fulfilled, (state, action) => {
    const result = action.payload;
    const params = action.meta.arg;
    // Handle case-level attributes
    if (params.entityType === 'case') {
      return { ...state, caseAttributes: result };
    }

    // Handle span-level attributes
    if (params.entityType === 'span') {
      const { invoices: updatedInvoices, looseSpans: updatedLooseSpans } =
        updateSpanRubricAttributes(state.invoices, state.looseSpans, Number(params.entityId),
          attrs => [
            ...attrs.filter(a => !matchesAttributeSpanScope(a, params.spanScopeAttributeId ?? params.parentQuestionId)),
            ...result
          ]
        );
      return { ...state, invoices: updatedInvoices, looseSpans: updatedLooseSpans };
    }

    // Only process code entity types (code_procedure, code_diagnosis, code_modifier)
    if (!params.entityType.startsWith('code_')) return state;

    const updatedInvoices = updateCodeAttributes(state.invoices, Number(params.entityId), params.entityType,
      attrs => mergeFetchedAttributes(attrs, result)
    );

    return { ...state, invoices: updatedInvoices };
  });

  builder.addCase(deleteAttribute.pending, (state, action) => {
    const params = action.meta.arg;
    const key = `${params.entityType}:${params.entityId}:${params.questionId}`;
    return {
      ...state,
      pendingAutosaves: { ...(state.pendingAutosaves ?? {}), [action.meta.requestId]: true as true },
      attributeLatestRequestId: { ...(state.attributeLatestRequestId ?? {}), [key]: action.meta.requestId }
    };
  });

  builder.addCase(deleteAttribute.rejected, (state, action) => {
    const pending = { ...(state.pendingAutosaves ?? {}) };
    delete pending[action.meta.requestId];
    return { ...state, pendingAutosaves: pending };
  });

  builder.addCase(deleteAttribute.fulfilled, (state, action) => {
    const params = action.meta.arg;
    const pending = { ...(state.pendingAutosaves ?? {}) };
    delete pending[action.meta.requestId];

    // Stale-guard: see updateAttribute.fulfilled above.
    const key = `${params.entityType}:${params.entityId}:${params.questionId}`;
    const latestRequestId = (state.attributeLatestRequestId ?? {})[key];
    if (latestRequestId !== undefined && action.meta.requestId !== latestRequestId) {
      return { ...state, pendingAutosaves: pending };
    }

    if (params.entityType === 'case') {
      const caseAttributes = (state.caseAttributes || []).filter(
        a => a.questionId !== params.questionId
      );
      return { ...state, pendingAutosaves: pending, caseAttributes };
    }

    // Handle span-level attributes
    if (params.entityType === 'span') {
      const { invoices: updatedInvoices, looseSpans: updatedLooseSpans } =
        updateSpanRubricAttributes(state.invoices, state.looseSpans, Number(params.entityId),
          attrs => attrs.filter(a =>
            !(a.questionId === params.questionId &&
              matchesAttributeSpanScope(a, params.spanScopeAttributeId ?? params.parentQuestionId))
          )
        );
      return { ...state, pendingAutosaves: pending, invoices: updatedInvoices, looseSpans: updatedLooseSpans };
    }

    // Only process code entity types (code_procedure, code_diagnosis, code_modifier)
    if (!params.entityType.startsWith('code_')) return { ...state, pendingAutosaves: pending };

    const updatedInvoices = updateCodeAttributes(state.invoices, Number(params.entityId), params.entityType,
      attrs => attrs.filter(a => a.questionId !== params.questionId)
    );

    return { ...state, pendingAutosaves: pending, invoices: updatedInvoices };
  });

  builder.addCase(clearDownstreamAttributes.type, (state, action: AnyAction) => {
    const params = action.payload;
    const questionIdSet = new Set(params.questionIds);

    if (params.entityType === 'case') {
      const caseAttributes = (state.caseAttributes || []).filter(
        a => !questionIdSet.has(a.questionId)
      );
      return { ...state, caseAttributes };
    }

    if (params.entityType === 'span') {
      const spanScopeAttributeId = params.spanScopeAttributeId ?? params.parentQuestionId;
      const { invoices: updatedInvoices, looseSpans: updatedLooseSpans } =
        updateSpanRubricAttributes(state.invoices, state.looseSpans, Number(params.entityId),
          attrs => attrs.filter(a => !(questionIdSet.has(a.questionId) && matchesAttributeSpanScope(a, spanScopeAttributeId)))
        );
      return { ...state, invoices: updatedInvoices, looseSpans: updatedLooseSpans };
    }

    if (!params.entityType.startsWith('code_')) return state;

    const updatedInvoices = updateCodeAttributes(
      state.invoices,
      Number(params.entityId),
      params.entityType,
      attrs => attrs.filter(a => !questionIdSet.has(a.questionId))
    );

    return { ...state, invoices: updatedInvoices };
  });

  builder.addCase(setValidationResult.type, (state, action: AnyAction) => ({
    ...state,
    validationResult: action.payload,
  }));

  builder.addCase(setCaseLevelComplete.type, (state, action: AnyAction) => ({
    ...state,
    caseLevelComplete: action.payload,
  }));

  builder.addCase(setPendingSpanNav.type, (state, action: AnyAction) => ({
    ...state,
    pendingSpanNavId: action.payload,
  }));

  builder.addCase(setCodeCompletion.type, (state, action: AnyAction) => {
    const { codeId, complete } = action.payload as {
      codeId: string;
      complete: boolean;
    };
    // No-op when unchanged to avoid extra renders of every code row.
    if ((state.codeCompletion ?? {})[codeId] === complete) {
      return state;
    }
    return {
      ...state,
      codeCompletion: { ...state.codeCompletion, [codeId]: complete },
    };
  });

  builder.addCase(setManualActiveCode.type, (state, action: AnyAction) => ({
    ...state,
    manualActiveCodeId: action.payload,
  }));

  builder.addCase(fetchAllAttributes.fulfilled, (state, action: AnyAction) => {
    const result = action.payload;

    const { caseAttributes, codeAttributesMap, spanAttributesMap } = result;

    // Update each code's attributes across all invoices
    // [CDMX-17055] look up code attributes by entityType+id (matches the action's
    // normKey) so same-id codes of different types don't clobber each other.
    const codeAttrKey = (entityType: string, id: number) => `${String(entityType).toUpperCase()}:${id}`;
    let updatedInvoices = updateAllCodes(state.invoices, (codes, entityType) =>
      codes.map(code => ({
        ...code,
        attributes: codeAttributesMap[codeAttrKey(entityType, code.id)] || code.attributes || []
      }))
    );

    // Update span rubricAttributes across invoices and looseSpans
    let updatedLooseSpans = state.looseSpans;
    if (spanAttributesMap) {
      // Update spans in code arrays within invoices
      updatedInvoices = updatedInvoices.map(invoice => ({
        ...invoice,
        transactions: invoice.transactions.map(transaction => ({
          ...transaction,
          procedures: transaction.procedures.map(code => ({
            ...code,
            spans: code.spans?.map(span => ({
              ...span,
              rubricAttributes: spanAttributesMap[span.id] || span.rubricAttributes || []
            })) || []
          })),
          diagnoses: transaction.diagnoses.map(code => ({
            ...code,
            spans: code.spans?.map(span => ({
              ...span,
              rubricAttributes: spanAttributesMap[span.id] || span.rubricAttributes || []
            })) || []
          })),
          modifiers: transaction.modifiers.map(code => ({
            ...code,
            spans: code.spans?.map(span => ({
              ...span,
              rubricAttributes: spanAttributesMap[span.id] || span.rubricAttributes || []
            })) || []
          })),
        }))
      }));

      // Update loose spans
      updatedLooseSpans = state.looseSpans.map(span => ({
        ...span,
        rubricAttributes: spanAttributesMap[span.id] || span.rubricAttributes || []
      }));
    }

    return {
      ...state,
      caseAttributes,
      invoices: updatedInvoices,
      looseSpans: updatedLooseSpans,
      attributesFetched: true,
      attributesFetchError: false
    };
  });

  builder.addCase(fetchAllAttributes.rejected, (state) => ({
    ...state,
    // [CDMX-17055] A failed attribute load must NOT mark attributes as fetched —
    // otherwise the bench renders blank rubric answers as if complete. Flag the
    // error so the UI can surface a retry instead of a silent empty state.
    attributesFetched: false,
    attributesFetchError: true
  }));
});

export { gradeBenchPanel };
